import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  XAxis,
  YAxis,
} from "recharts"
import {
  IconFileCheck,
  IconFilePlus,
  IconLoader,
  IconPercentage,
  IconTrendingDown,
  IconTrendingUp,
} from "@tabler/icons-react"

import { api } from "@/lib/api"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group"

// ── Types ────────────────────────────────────────────────────────────

interface DailySummary {
  date: string
  benefitProgramId: number
  benefitProgramYear: number
  createdCount: number
  certifiedCount: number
  inProgressCount: number
}

interface AggregatedDay {
  date: string
  created: number
  certified: number
  inProgress: number
}

interface ProgramBreakdown {
  programName: string
  benefitProgramYear: number
  createdCount: number
  certifiedCount: number
  inProgressCount: number
  avgDaysToComplete: number
  totalGrantAmount: number
}

// ── Helpers ──────────────────────────────────────────────────────────

const RANGE_DAYS: Record<string, number> = {
  "7d": 7,
  "28d": 28,
  "90d": 90,
}

function aggregateByDate(raw: DailySummary[]): AggregatedDay[] {
  const map = new Map<string, AggregatedDay>()
  for (const r of raw) {
    const existing = map.get(r.date)
    if (existing) {
      existing.created += r.createdCount
      existing.certified += r.certifiedCount
      existing.inProgress += r.inProgressCount
    } else {
      map.set(r.date, {
        date: r.date,
        created: r.createdCount,
        certified: r.certifiedCount,
        inProgress: r.inProgressCount,
      })
    }
  }
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null
  return Math.round(((current - previous) / previous) * 100)
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

// ── Chart config ─────────────────────────────────────────────────────

const flowChartConfig = {
  created: {
    label: "Created",
    color: "var(--chart-1)",
  },
  certified: {
    label: "Certified",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig

const backlogChartConfig = {
  inProgress: {
    label: "Pending Backlog",
    color: "var(--chart-3)",
  },
} satisfies ChartConfig

// ── Main component ───────────────────────────────────────────────────

export default function DashboardPage() {
  const [timeRange, setTimeRange] = useState("28d")
  const [data, setData] = useState<AggregatedDay[]>([])
  const [programData, setProgramData] = useState<ProgramBreakdown[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch enough data for the selected range plus the prior period for trend comparison
      const days = RANGE_DAYS[timeRange] ?? 28
      const totalDays = days * 2
      const start = new Date()
      start.setDate(start.getDate() - totalDays)
      const startStr = start.toISOString().split("T")[0]

      const rangeStart = new Date()
      rangeStart.setDate(rangeStart.getDate() - days)
      const rangeStartStr = rangeStart.toISOString().split("T")[0]

      const [raw, programs] = await Promise.all([
        api.get<DailySummary[]>(
          `/api/statistics/daily-benefit-applications?startDate=${startStr}`
        ),
        api.get<ProgramBreakdown[]>(
          `/api/statistics/program-breakdown?startDate=${rangeStartStr}`
        ),
      ])
      setData(aggregateByDate(raw))
      setProgramData(programs)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load statistics")
    } finally {
      setLoading(false)
    }
  }, [timeRange])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Split data into current period and previous period for trend comparison
  const { currentData, prevData } = useMemo(() => {
    const days = RANGE_DAYS[timeRange] ?? 28
    const now = new Date()
    const cutoff = new Date(now)
    cutoff.setDate(cutoff.getDate() - days)
    const prevCutoff = new Date(cutoff)
    prevCutoff.setDate(prevCutoff.getDate() - days)

    const cutoffStr = cutoff.toISOString().split("T")[0]!
    const prevCutoffStr = prevCutoff.toISOString().split("T")[0]!

    return {
      currentData: data.filter((d) => d.date >= cutoffStr),
      prevData: data.filter((d) => d.date >= prevCutoffStr && d.date < cutoffStr),
    }
  }, [data, timeRange])

  // Identify contiguous weekend (Sat/Sun) spans for background shading
  const weekendRanges = useMemo(() => {
    const ranges: { x1: string; x2: string }[] = []
    let spanStart: string | null = null
    for (const d of currentData) {
      const dow = new Date(d.date + "T00:00:00").getDay()
      const isWeekend = dow === 0 || dow === 6
      if (isWeekend && !spanStart) {
        spanStart = d.date
      } else if (!isWeekend && spanStart) {
        ranges.push({ x1: spanStart, x2: currentData[currentData.indexOf(d) - 1]!.date })
        spanStart = null
      }
    }
    if (spanStart) {
      ranges.push({ x1: spanStart, x2: currentData[currentData.length - 1]!.date })
    }
    return ranges
  }, [currentData])

  // Aggregate totals for cards
  const stats = useMemo(() => {
    const sum = (arr: AggregatedDay[], key: keyof AggregatedDay) =>
      arr.reduce((acc, d) => acc + (d[key] as number), 0)
    const curCreated = sum(currentData, "created")
    const curCertified = sum(currentData, "certified")
    const prevCreated = sum(prevData, "created")
    const prevCertified = sum(prevData, "certified")
    const latestInProgress =
      currentData.length > 0 ? currentData[currentData.length - 1]!.inProgress : 0
    const prevLatestInProgress =
      prevData.length > 0 ? prevData[prevData.length - 1]!.inProgress : 0

    const curRate = curCreated > 0 ? (curCertified / curCreated) * 100 : 0
    const prevRate = prevCreated > 0 ? (prevCertified / prevCreated) * 100 : 0

    return {
      created: curCreated,
      createdChange: pctChange(curCreated, prevCreated),
      certified: curCertified,
      certifiedChange: pctChange(curCertified, prevCertified),
      inProgress: latestInProgress,
      inProgressChange: pctChange(latestInProgress, prevLatestInProgress),
      certRate: Math.round(curRate),
      certRateChange: prevRate > 0 ? Math.round(curRate - prevRate) : null,
    }
  }, [currentData, prevData])

  const rangeLabel =
    timeRange === "7d"
      ? "last 7 days"
      : timeRange === "28d"
        ? "last 4 weeks"
        : "last 3 months"

  if (loading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-8 w-20" />
              </CardHeader>
              <CardFooter>
                <Skeleton className="h-4 w-40" />
              </CardFooter>
            </Card>
          ))}
        </div>
        <div className="px-4 lg:px-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Dashboard</CardTitle>
            <CardDescription>Unable to load statistics: {error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      {/* ── Summary cards ── */}
      <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
        <StatCard
          label="Applications Created"
          value={stats.created.toLocaleString()}
          change={stats.createdChange}
          footer={`New applications ${rangeLabel}`}
          icon={<IconFilePlus className="size-4" />}
        />
        <StatCard
          label="Applications Certified"
          value={stats.certified.toLocaleString()}
          change={stats.certifiedChange}
          footer={`Certifications completed ${rangeLabel}`}
          icon={<IconFileCheck className="size-4" />}
        />
        <StatCard
          label="Currently In Progress"
          value={stats.inProgress.toLocaleString()}
          change={stats.inProgressChange}
          invertTrend
          footer="Pending applications as of latest date"
          icon={<IconLoader className="size-4" />}
        />
        <StatCard
          label="Certification Rate"
          value={`${stats.certRate}%`}
          change={stats.certRateChange}
          suffix="pp"
          footer={`Certified ÷ created ${rangeLabel}`}
          icon={<IconPercentage className="size-4" />}
        />
      </div>

      {/* ── Bar chart: daily created & certified ── */}
      <div className="px-4 lg:px-6">
        <Card className="@container/card">
          <CardHeader>
            <CardTitle>Daily Benefit Grant Activity</CardTitle>
            <CardDescription>
              <span className="hidden @[540px]/card:block">
                Applications created and certified per day — {rangeLabel}
              </span>
              <span className="@[540px]/card:hidden">
                Grant activity — {rangeLabel}
              </span>
            </CardDescription>
            <CardAction>
              <ToggleGroup
                type="single"
                value={timeRange}
                onValueChange={(v) => v && setTimeRange(v)}
                variant="outline"
                className="hidden *:data-[slot=toggle-group-item]:!px-4 @[767px]/card:flex"
              >
                <ToggleGroupItem value="7d">7 days</ToggleGroupItem>
                <ToggleGroupItem value="28d">4 weeks</ToggleGroupItem>
                <ToggleGroupItem value="90d">3 months</ToggleGroupItem>
              </ToggleGroup>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger
                  className="flex w-40 **:data-[slot=select-value]:block **:data-[slot=select-value]:truncate @[767px]/card:hidden"
                  size="sm"
                  aria-label="Select time range"
                >
                  <SelectValue placeholder="4 weeks" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="7d" className="rounded-lg">7 days</SelectItem>
                  <SelectItem value="28d" className="rounded-lg">4 weeks</SelectItem>
                  <SelectItem value="90d" className="rounded-lg">3 months</SelectItem>
                </SelectContent>
              </Select>
            </CardAction>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
            <ChartContainer
              config={flowChartConfig}
              className="aspect-auto h-[280px] w-full"
            >
              <BarChart data={currentData}>
                <CartesianGrid vertical={false} />
                {weekendRanges.map((r, i) => (
                  <ReferenceArea
                    key={i}
                    x1={r.x1}
                    x2={r.x2}
                    fill="currentColor"
                    className="text-muted-foreground/10"
                    fillOpacity={1}
                    ifOverflow="hidden"
                  />
                ))}
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={formatDate}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  width={40}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })
                      }
                      indicator="dot"
                    />
                  }
                />
                <Bar
                  dataKey="created"
                  fill="var(--color-created)"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="certified"
                  fill="var(--color-certified)"
                  radius={[4, 4, 0, 0]}
                />
                <ChartLegend content={<ChartLegendContent />} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* ── Pending backlog trend ── */}
      <div className="px-4 lg:px-6">
        <Card className="@container/card">
          <CardHeader>
            <CardTitle>Pending Backlog Trend</CardTitle>
            <CardDescription>
              Total applications awaiting certification — {rangeLabel}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
            <ChartContainer
              config={backlogChartConfig}
              className="aspect-auto h-[200px] w-full"
            >
              <AreaChart data={currentData}>
                <defs>
                  <linearGradient id="fillBacklog" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-inProgress)" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="var(--color-inProgress)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={formatDate}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  width={48}
                  domain={["dataMin - 200", "dataMax + 200"]}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })
                      }
                      indicator="line"
                    />
                  }
                />
                <Area
                  dataKey="inProgress"
                  type="monotone"
                  fill="url(#fillBacklog)"
                  stroke="var(--color-inProgress)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
      {/* ── Program breakdown table ── */}
      <div className="px-4 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Program Breakdown</CardTitle>
            <CardDescription>
              Benefit grant statistics by program — {rangeLabel}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {programData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No grant data for this period.
              </p>
            ) : (
              <div className="overflow-x-auto" role="region" aria-label="Program breakdown" tabIndex={0}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Program</th>
                      <th className="pb-2 pr-4 font-medium text-right">Year</th>
                      <th className="pb-2 pr-4 font-medium text-right">Created</th>
                      <th className="pb-2 pr-4 font-medium text-right">Certified</th>
                      <th className="pb-2 pr-4 font-medium text-right">Pending</th>
                      <th className="pb-2 pr-4 font-medium text-right">Avg Days</th>
                      <th className="pb-2 font-medium text-right">Total Granted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programData.map((p) => (
                      <tr
                        key={`${p.programName}-${p.benefitProgramYear}`}
                        className="border-b last:border-0"
                      >
                        <td className="py-2.5 pr-4 font-medium">{p.programName}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{p.benefitProgramYear}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{p.createdCount.toLocaleString()}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{p.certifiedCount.toLocaleString()}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{p.inProgressCount.toLocaleString()}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">
                          {p.avgDaysToComplete > 0 ? `${p.avgDaysToComplete}d` : "—"}
                        </td>
                        <td className="py-2.5 text-right tabular-nums font-medium">
                          ${p.totalGrantAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr className="font-semibold border-t-2">
                      <td className="py-2.5 pr-4" colSpan={2}>Total</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">
                        {programData.reduce((s, p) => s + p.createdCount, 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">
                        {programData.reduce((s, p) => s + p.certifiedCount, 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">
                        {programData.reduce((s, p) => s + p.inProgressCount, 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">
                        {(() => {
                          const withDays = programData.filter((p) => p.avgDaysToComplete > 0)
                          return withDays.length > 0
                            ? `${(withDays.reduce((s, p) => s + p.avgDaysToComplete, 0) / withDays.length).toFixed(1)}d`
                            : "—"
                        })()}
                      </td>
                      <td className="py-2.5 text-right tabular-nums">
                        ${programData.reduce((s, p) => s + p.totalGrantAmount, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// ── Stat card component ──────────────────────────────────────────────

function StatCard({
  label,
  value,
  change,
  footer,
  icon,
  invertTrend = false,
  suffix = "%",
}: {
  label: string
  value: string
  change: number | null
  footer: string
  icon: React.ReactNode
  invertTrend?: boolean
  suffix?: string
}) {
  const isPositive = change !== null && change >= 0
  const trendGood = invertTrend ? !isPositive : isPositive
  const TrendIcon = isPositive ? IconTrendingUp : IconTrendingDown

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
          {value}
        </CardTitle>
        {change !== null && (
          <CardAction>
            <Badge variant="outline" className={trendGood ? "text-emerald-600" : "text-rose-600"}>
              <TrendIcon className="size-3" />
              {isPositive ? "+" : ""}
              {change}
              {suffix}
            </Badge>
          </CardAction>
        )}
      </CardHeader>
      <CardFooter className="flex-col items-start gap-1.5 text-sm">
        <div className="line-clamp-1 flex gap-2 font-medium">
          {icon} {footer}
        </div>
        <div className="text-muted-foreground">vs. prior period</div>
      </CardFooter>
    </Card>
  )
}

